// -----------------------------------------------------------------------------
// src/pages/campaigns/CampaignCreatorSpecific.js
// -----------------------------------------------------------------------------
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Avatar,
  Paper,
  Chip,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Stack,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"; // 🆕
import { useMutation, useQuery, useQueryClient } from "react-query";
import client from "../../../../../API";
import useAlertDialog from "../../../../../Components/useAlertDialog";
import AlertDialog from "../../../../../Components/AlertDialog";
import ChatScreen from "../../../../../Components/ChatScreen";
import ParticipantsPanel from "../../../../../Components/ChatComponents/chatextra";

/**
 * CampaignCreatorSpecific – mobile-optimised revision with back button.
 */
const CampaignCreatorSpecific = () => {
  // -------------------------------------------------------------------------
  // Hooks & params
  // -------------------------------------------------------------------------
  const queryClient = useQueryClient();
  const { campaignId, creatorName } = useParams();
  const navigate = useNavigate();
  const { dialogState, openDialog } = useAlertDialog();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600 px

  // Drawer state for participants
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const toggleParticipants = () => setParticipantsOpen((p) => !p);

  // NEW: deliverable badge count + ChatScreen ref
  const [ineligibleCount, setIneligibleCount] = useState(0);
  const chatRef = useRef(null);

  // -------------------------------------------------------------------------
  // Campaign + creator queries
  // -------------------------------------------------------------------------
  const {
    data: campaignData,
    isLoading: campaignLoading,
    error: campaignError,
  } = useQuery(
    ["campaign", campaignId],
    () => client.campaigns.details(campaignId),
    { enabled: !!campaignId }
  );

  const creatorCampaignData = useMemo(() => {
    if (!campaignData) return undefined;
    return campaignData.creators.find((c) => c.id === creatorName);
  }, [campaignData, creatorName]);

  const {
    data: creatorData,
    isLoading: creatorLoading,
    error: creatorError,
  } = useQuery(
    ["creator", creatorName],
    () => client.creators.fetchDetails(creatorName),
    { enabled: !!creatorName && !!creatorCampaignData }
  );

  // -------------------------------------------------------------------------
  // Conversation bootstrap
  // -------------------------------------------------------------------------
  const {
    data: dataCheck,
    refetch: refetchDataCheck,
  } = useQuery(
    ["checkConversation", campaignId, creatorData?.creator?.creator],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaignId,
        creator_id: creatorData.creator.creator,
      }),
    { enabled: !!campaignData && !!creatorData }
  );

  const conversationId = dataCheck?.conversation_id;

  const createConversation = useMutation(client.conversations.createCampaign, {
    onSuccess: () => refetchDataCheck(),
    onError: (e) => openDialog("Error", e.response?.data?.error || e.message),
  });

  useEffect(() => {
    if (dataCheck && !conversationId && !createConversation.isLoading) {
      createConversation.mutate({
        campaign_id: campaignId,
        creator_id: creatorData.creator.creator,
      });
    }
  }, [dataCheck, conversationId]);

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------
  if (campaignLoading || creatorLoading) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading…</Typography>
      </Box>
    );
  }
  if (campaignError)
    return (
      <Typography>Error loading campaign: {campaignError.message}</Typography>
    );
  if (creatorError)
    return (
      <Typography>Error loading creator: {creatorError.message}</Typography>
    );

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------
  const videoList =
    creatorCampaignData?.videos.filter((v) => v.parentId == null) || [];
  const crosspostList =
    creatorCampaignData?.videos.filter((v) => v.parentId != null) || [];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AlertDialog alertState={dialogState} />

      {/* Header */}
      <Paper
        elevation={4}
        square
        sx={{
          p: 2,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          alignItems={isMobile ? "flex-start" : "center"}
          justifyContent="space-between"
        >
          {/* Left side */}
          <Stack direction="row" spacing={2} alignItems="center" flexShrink={1}>
            {/* Back button */}
            <IconButton
              edge="start"
              onClick={() => navigate(`/editcampaign/${campaignId}`)}
              sx={{ mr: 1 }}
              aria-label="Back to campaign editor"
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Creator avatar */}
            <Avatar
              src={creatorData.creator.pfphref}
              alt={creatorData.creator.creator}
              sx={{ width: 48, height: 48 }}
            />

            {/* Title */}
            <Typography
              variant="h6"
              noWrap={!isMobile}
              sx={{
                maxWidth: isMobile ? "100%" : "45vw",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Campaign with @{creatorData.creator.display_name} –{" "}
              {campaignData.name}
            </Typography>
          </Stack>

          {/* Right-side icons */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Deliverables badge */}
            <IconButton
              color="secondary"
              size="large"
              disabled={ineligibleCount === 0}
              onClick={() =>
                chatRef.current?.scrollToFirstIneligibleVideo?.()
              }
              aria-label="Show ineligible deliverables"
            >
              <Badge
                badgeContent={ineligibleCount}
                color="error"
                invisible={ineligibleCount === 0}
              >
                <AssignmentTurnedInIcon />
              </Badge>
            </IconButton>

            {/* Add participant */}
            {conversationId && (
              <IconButton
                color="primary"
                size="large"
                onClick={toggleParticipants}
                sx={{ alignSelf: isMobile ? "flex-end" : "center" }}
              >
                <PersonAddIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Description block */}
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Description:</strong> {campaignData.description}
          <br />
          <strong>Creator Brief:</strong> {creatorCampaignData.brief}
        </Typography>
      </Paper>

      {/* Chat */}
      <ChatScreen
        ref={chatRef}
        conversationId={conversationId}
        creatorId={creatorData.creator.creator}
        sx={{ flex: 1, minHeight: 0 }}
        /* 🆕 keep badge count in sync */
        onDeliverablesUpdate={(list) => setIneligibleCount(list.length)}
      >
        {/* Chips row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mb: 2,
            width: "100%",
          }}
        >
          <Chip
            color="primary"
            label={`Offer: ${creatorCampaignData.price.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          />

          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            Promotions (x{videoList.length}):
          </Typography>
          {videoList.map((v) => (
            <Chip
              key={v.id}
              color="primary"
              label={`${v.promotionPlatform} – ${v.promotionType}`}
            />
          ))}

          {crosspostList.length > 0 && (
            <Typography variant="subtitle2" sx={{ ml: 1, mr: 1 }}>
              Crossposts:
            </Typography>
          )}
          {crosspostList.map((v) => (
            <Chip key={v.id} color="secondary" label={v.promotionPlatform} />
          ))}
        </Box>
      </ChatScreen>

      {/* Participants drawer – full-width on mobile */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={participantsOpen}
        onClose={toggleParticipants}
        PaperProps={{
          sx: isMobile
            ? { height: "80vh", borderTopLeftRadius: 8, borderTopRightRadius: 8 }
            : { width: 340 },
        }}
      >
        <ParticipantsPanel
          conversationId={conversationId}
          onClose={toggleParticipants}
        />
      </Drawer>
    </Box>
  );
};

export default CampaignCreatorSpecific;
